import { getLocalizedValue } from "@repo/shared";
/**
 * Sustainability status widget (QP-179) — pure, shared by the advisor wizard
 * and the public flow renderer.
 *
 * Renders ONE portfolio's matching table (rows = products + a portfolio summary
 * row; columns = configured criteria + per-product Meets) and its status line
 * (QP-179 — the status component is portfolio-scoped, rendered once per
 * portfolio on the portfolio page). On the advisor surface, when the component
 * config allows overrides (allowOverride, default true), a deviation exposes the
 * Accept / Do-not-proceed decision controls with an optional justification. When
 * allowOverride is false, no decision controls are shown — a deviation must be
 * resolved by changing preferences or the portfolio. The public surface never
 * shows decision controls (canDecide=false).
 */

import type {
  PortfolioSustainabilityStateResponse,
  SustainabilityActualValue,
  SustainabilityDecisionValue,
  SustainabilityRequiredValue,
  SustainabilitySnapshotData,
  Translated,
} from "@repo/shared/types";
import { sustainabilityShortLabel } from "@repo/shared/types";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { InlineSvg } from "@/components/ui/inline-svg";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./sustainability-status-widget.i18n";

type SustainabilityStatusT = Translated<typeof T>;

export type SustainabilityStatusWidgetProps = {
  state: PortfolioSustainabilityStateResponse | null;
  isLoading?: boolean;
  /** Advisor + editable instance → the deviation decision controls render. */
  canDecide?: boolean;
  /**
   * When false, the deviation decision controls are suppressed even for the
   * advisor; a deviation must be resolved by changing preferences/portfolio.
   * Default true. The public surface never shows controls regardless.
   */
  allowOverride?: boolean;
  onDecide?: (decision: SustainabilityDecisionValue, justification: string) => void;
  decisionPending?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
};

function statusBadge(
  t: SustainabilityStatusT,
  effective: "ok" | "accepted" | "not_accepted" | "deviation",
) {
  switch (effective) {
    case "ok":
      return <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">{t.statusOk}</Badge>;
    case "accepted":
      return (
        <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">{t.statusAccepted}</Badge>
      );
    case "not_accepted":
      return <Badge variant="destructive">{t.statusNotAccepted}</Badge>;
    default:
      return <Badge variant="destructive">{t.statusDeviation}</Badge>;
  }
}

/** Header sub-label: the required step's label for scales, "✓" for product preferences. */
function requiredLabel(
  required: SustainabilityRequiredValue,
  language: string,
  fallbackLanguage: string,
): string {
  return required.kind === "scale" || required.kind === "sfdr"
    ? `≥ ${getLocalizedValue(required.labelI18n, language, fallbackLanguage)}`
    : "✓";
}

function criterionCell(
  actual: SustainabilityActualValue,
  meets: boolean,
  language: string,
  fallbackLanguage: string,
) {
  // No data ("not reported") is neutral — gray dash, never a red failure.
  if (actual === null) {
    return (
      <span className="text-muted-foreground" data-meets="unknown">
        —
      </span>
    );
  }
  const value =
    actual.kind === "product_preference"
      ? actual.value
        ? "✓"
        : "✗"
      : getLocalizedValue(actual.labelI18n, language, fallbackLanguage);
  return (
    <span className={meets ? "text-emerald-700" : "text-destructive"} data-meets={meets}>
      {value}
    </span>
  );
}

function PortfolioTable({
  snapshot,
  portfolioName,
  language,
  t,
}: {
  snapshot: SustainabilitySnapshotData;
  portfolioName: string;
  language: string;
  t: SustainabilityStatusT;
}) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const criteria = snapshot.criteria;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{portfolioName}</span>
        <span className="text-xs text-muted-foreground">
          {snapshot.mode === "portfolio"
            ? t.modePortfolio(String(snapshot.thresholdPct ?? ""))
            : t.modeIndividual}
        </span>
      </div>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.product}</TableHead>
            <TableHead className="text-right">{t.weight}</TableHead>
            {criteria.map((c) => (
              <TableHead key={c.id} className="text-center">
                <span className="inline-flex items-center justify-center gap-1">
                  {c.iconSvg && (
                    <InlineSvg
                      svg={c.iconSvg}
                      className="[&_svg]:h-3.5 [&_svg]:w-3.5 text-muted-foreground"
                    />
                  )}
                  {c.kind === "sfdr"
                    ? t.sfdr
                    : sustainabilityShortLabel(c.shortLabelI18n, language) ||
                      getLocalizedValue(c.labelI18n, language, tenantDefaultLanguage) ||
                      c.key ||
                      c.id}
                </span>
                <span className="block text-[10px] font-normal text-muted-foreground">
                  {requiredLabel(c.required, language, tenantDefaultLanguage)}
                </span>
              </TableHead>
            ))}
            <TableHead className="text-center">{t.meets}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {snapshot.products.map((p) => (
            <TableRow key={p.productId}>
              <TableCell>{p.name}</TableCell>
              <TableCell className="text-right">{p.weight.toFixed(1)}%</TableCell>
              {criteria.map((c) => {
                const result = p.perCriterion.find((r) => r.criterionId === c.id);
                return (
                  <TableCell key={c.id} className="text-center">
                    {result
                      ? criterionCell(result.actual, result.meets, language, tenantDefaultLanguage)
                      : "—"}
                  </TableCell>
                );
              })}
              <TableCell className="text-center">
                {p.meets ? (
                  <Check className="h-4 w-4 text-emerald-600 inline" aria-label={t.meets} />
                ) : (
                  <X className="h-4 w-4 text-destructive inline" aria-label={t.doesNotMeet} />
                )}
              </TableCell>
            </TableRow>
          ))}
          {/* Portfolio summary row */}
          <TableRow className="font-medium bg-muted/40">
            <TableCell>{t.portfolioRow}</TableCell>
            {/* QL-414 — the portfolio's own weight, as in the PDF; the share
                meeting the preferences is the labelled cell beside it. */}
            <TableCell className="text-right">100.0%</TableCell>
            <TableCell colSpan={criteria.length} className="text-xs text-muted-foreground">
              {t.weightMeeting(snapshot.weightMeetingPct.toFixed(1))}
            </TableCell>
            <TableCell className="text-center">
              {snapshot.status === "ok" ? (
                <Check className="h-4 w-4 text-emerald-600 inline" aria-label={t.statusOk} />
              ) : (
                <X className="h-4 w-4 text-destructive inline" aria-label={t.statusDeviation} />
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export function SustainabilityStatusWidget({
  state,
  isLoading = false,
  canDecide = false,
  allowOverride = true,
  onDecide,
  decisionPending = false,
  style = "none",
  title,
  description,
  language = "en_GB",
  hasValidationError = false,
}: SustainabilityStatusWidgetProps) {
  const t = useT(T);

  const [decision, setDecision] = useState<SustainabilityDecisionValue | "">("");
  const [justification, setJustification] = useState("");

  // Pre-fill from a stored (valid) decision; reset when it is voided.
  useEffect(() => {
    setDecision(state?.decision?.decision ?? "");
    setJustification(state?.decision?.justification ?? "");
  }, [state?.decision?.decision, state?.decision?.justification]);

  if (isLoading) return null;

  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : style === "border"
      ? "border rounded-lg p-4"
      : style === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  // Not enabled: config absent, gate unanswered, or important = no → muted hint.
  if (!state || !state.enabled) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notApplicable}
      </div>
    );
  }

  const evaluable = state.status !== null;

  return (
    <div className={wrapperClass} data-testid="sustainability-status-widget">
      <div className="space-y-4">
        <FlowComponentHeader title={title} description={description} />
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {!evaluable || !state.snapshot ? (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.notEvaluable}
          </div>
        ) : (
          <>
            <PortfolioTable
              snapshot={state.snapshot}
              portfolioName={state.portfolioName}
              language={language}
              t={t}
            />

            <div className="flex items-center gap-2" data-testid="sustainability-portfolio-status">
              <span className="text-sm font-medium">{t.statusLabel}:</span>
              {statusBadge(t, state.effective ?? "deviation")}
            </div>

            {/* Recorded decision (both surfaces show the outcome; only the
                advisor can change it). */}
            {state.decision && !(canDecide && allowOverride) && (
              <p className="text-xs text-muted-foreground">
                {t.decisionSavedBy}
                {state.decision.justification
                  ? ` — ${t.justification}: ${state.decision.justification}`
                  : ""}
              </p>
            )}

            {/* allowOverride=false + deviation → read-only note (no controls). */}
            {!allowOverride && state.status === "deviation" && !state.decision && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="sustainability-no-override-note"
              >
                {t.deviationNoOverride}
              </p>
            )}

            {/* Advisor-only deviation decision (gated by allowOverride) */}
            {canDecide && allowOverride && state.status === "deviation" && (
              <div className="space-y-3 border rounded-lg p-4">
                <div>
                  <Label className="text-base">{t.decisionTitle}</Label>
                  <p className="text-xs text-muted-foreground">{t.decisionDescription}</p>
                </div>
                <RadioGroup
                  value={decision}
                  onValueChange={(v) => setDecision(v as SustainabilityDecisionValue)}
                  disabled={decisionPending}
                  className="flex gap-6"
                >
                  <OptionRow>
                    <RadioGroupItem value="accepted" id="sustainability-decision-accept" />
                    <Label
                      htmlFor="sustainability-decision-accept"
                      className="font-normal cursor-pointer"
                    >
                      {t.decisionAccept}
                    </Label>
                  </OptionRow>
                  <OptionRow>
                    <RadioGroupItem
                      value="do_not_proceed"
                      id="sustainability-decision-do-not-proceed"
                    />
                    <Label
                      htmlFor="sustainability-decision-do-not-proceed"
                      className="font-normal cursor-pointer"
                    >
                      {t.decisionDoNotProceed}
                    </Label>
                  </OptionRow>
                </RadioGroup>
                <div className="space-y-2">
                  <Label htmlFor="sustainability-decision-justification">
                    {t.justificationLabel}
                  </Label>
                  <Textarea
                    id="sustainability-decision-justification"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    disabled={decisionPending}
                    rows={2}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => decision && onDecide?.(decision, justification)}
                  disabled={!decision}
                  loading={decisionPending}
                >
                  {t.save}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
